/**
 * FloatingFacebookPill — sticky bottom-right floating FB icon.
 * On hover (desktop) or tap (mobile), it expands into a compact card
 * showing the live FB follow widget for facebook.com/Optivize.SM.
 *
 * Positioned to coexist with any existing chat fab (offset higher).
 */
import React, { useState, useEffect } from "react";

const FB_URL = "https://www.facebook.com/Optivize.SM";
const FOLLOW_SRC =
  "https://www.facebook.com/plugins/page.php?" +
  "href=" + encodeURIComponent(FB_URL) +
  "&tabs=&width=320&height=150" +
  "&small_header=false&adapt_container_width=true" +
  "&hide_cover=false&show_facepile=true&appId=";

export default function FloatingFacebookPill() {
  const [open, setOpen] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // Close on escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div
      className="fixed z-40"
      style={{bottom:"96px", right:"20px"}}
      data-testid="floating-fb-pill"
    >
      {/* Expanded card */}
      {open && (
        <div
          className="absolute bottom-16 right-0 w-[340px] rounded-2xl shadow-2xl border border-blue-500/30 bg-slate-900 overflow-hidden"
          style={{animation:"fbPopIn 0.25s ease-out"}}
          data-testid="floating-fb-card"
          onMouseLeave={() => setOpen(false)}
        >
          <div className="px-4 py-3 flex items-center justify-between border-b border-slate-800"
               style={{background:"#1877F2"}}>
            <div className="flex items-center gap-2 text-white">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/></svg>
              <span className="font-semibold text-sm">Follow Optivize on Facebook</span>
            </div>
            <button onClick={() => setOpen(false)}
                    className="text-white/80 hover:text-white text-lg leading-none px-1"
                    aria-label="Close" data-testid="floating-fb-close">×</button>
          </div>
          <div className="bg-white">
            {!iframeLoaded && (
              <div className="h-[150px] flex items-center justify-center text-slate-400 text-xs">Loading…</div>
            )}
            <iframe
              src={FOLLOW_SRC}
              width="320" height="150"
              style={{border:"none", overflow:"hidden", width:"100%", display:iframeLoaded ? "block" : "none"}}
              scrolling="no" frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              title="Optivize on Facebook"
              loading="lazy"
              onLoad={() => setIframeLoaded(true)}
            />
          </div>
          <a
            href={FB_URL} target="_blank" rel="noopener noreferrer"
            className="block px-4 py-3 text-center text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            style={{background:"linear-gradient(90deg, #1877F2 0%, #1565d8 100%)"}}
            data-testid="floating-fb-visit"
          >
            Visit Our Page →
          </a>
        </div>
      )}

      {/* The pill button */}
      <button
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setOpen(true)}
        className="group relative flex items-center justify-center w-14 h-14 rounded-full shadow-xl transition-transform hover:scale-110"
        style={{
          background:"#1877F2",
          boxShadow:"0 8px 24px rgba(24,119,242,0.45), 0 0 0 4px rgba(24,119,242,0.15)",
        }}
        aria-label="Follow us on Facebook"
        data-testid="floating-fb-btn"
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="white">
          <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/>
        </svg>
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full border-2 border-blue-300/60 animate-ping pointer-events-none"
              style={{animationDuration:"2.5s"}}/>
      </button>

      <style>{`
        @keyframes fbPopIn {
          from { opacity: 0; transform: translateY(8px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
      `}</style>
    </div>
  );
}
